import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectUsers } from '../State/usersSlice';
import { getAuth } from 'firebase/auth';

/**
 * useAuthRecovery - A hook that monitors auth state consistency and recovers from flickers.
 *
 * This hook provides an additional layer of protection against Firebase auth state flickers.
 * It monitors for situations where:
 * 1. Redux has no user but localStorage has cached auth
 * 2. Firebase auth.currentUser exists but Redux doesn't have the user
 *
 * When inconsistencies are detected, it attempts recovery by restoring from cache.
 *
 * Usage: Add to App.tsx or any root component
 */
export function useAuthRecovery() {
    const { currentUser } = useSelector(selectUsers);
    const recoveryAttemptedRef = useRef(false);
    const lastKnownUserRef = useRef<string | null>(null);
    // Prevent repeating the network-error warning on every interval tick
    const periodicErrorLoggedRef = useRef(false);

    // Track last known user for detecting flickers
    useEffect(() => {
        if (currentUser?.id) {
            lastKnownUserRef.current = currentUser.id;
            recoveryAttemptedRef.current = false;
            periodicErrorLoggedRef.current = false; // reset when user is back
        }
    }, [currentUser?.id]);

    // Monitor for auth state inconsistencies (one-shot, on flicker)
    useEffect(() => {
        // Only check if we had a user and now we don't (potential flicker)
        if (currentUser) return;
        if (!lastKnownUserRef.current) return; // Never had a user in this session
        if (recoveryAttemptedRef.current) return; // Already attempted recovery

        const checkAndRecover = async () => {
            const auth = getAuth();

            // Check 1: Firebase still has a user — use silent refresh first to avoid
            // network call when token is still valid in memory.
            if (auth.currentUser) {
                console.log('useAuthRecovery: Redux has no user but Firebase does. Triggering token refresh...');
                try {
                    // Silent check — does NOT hit the network if token is fresh
                    await auth.currentUser.getIdToken(false);
                    // The onAuthStateChanged in App.tsx should pick this up automatically
                } catch (err: any) {
                    // Only attempt a forced network refresh if the token appears expired
                    if (err?.code !== 'auth/network-request-failed' && navigator.onLine) {
                        try {
                            await auth.currentUser.getIdToken(true);
                        } catch (netErr) {
                            console.warn('useAuthRecovery: Token refresh skipped (network unavailable or service worker issue)');
                        }
                    }
                }
                return;
            }

            // Check 2: localStorage has cached auth
            const cachedAuth = localStorage.getItem('cachedAuth');
            if (cachedAuth) {
                try {
                    const parsed = JSON.parse(cachedAuth);
                    if (parsed && parsed.uid === lastKnownUserRef.current) {
                        console.log('useAuthRecovery: Detected potential auth flicker. Cache matches last known user.');
                        recoveryAttemptedRef.current = true;
                    }
                } catch (e) {
                    // Parsing failed — ignore
                }
            }
        };

        // Delay check to allow natural auth state restoration
        const timeoutId = setTimeout(checkAndRecover, 500);
        return () => clearTimeout(timeoutId);
    }, [currentUser]);

    // Periodic consistency check (every 30 seconds)
    useEffect(() => {
        const intervalId = setInterval(async () => {
            const auth = getAuth();

            // If Firebase has a user but Redux doesn't, something is wrong
            if (auth.currentUser && !currentUser) {
                // Only log the warning once per inconsistency episode
                if (!periodicErrorLoggedRef.current) {
                    console.warn('useAuthRecovery: Periodic check found inconsistency - Firebase has user but Redux does not');
                    periodicErrorLoggedRef.current = true;
                }

                // Skip network refresh entirely when offline or service worker is blocking
                if (!navigator.onLine) return;

                try {
                    // Silent refresh — only hits network if the cached token is expired
                    await auth.currentUser.getIdToken(false);
                } catch (err: any) {
                    // Token is stale and we're online — attempt a real refresh once
                    if (err?.code !== 'auth/network-request-failed') {
                        auth.currentUser?.getIdToken(true).catch(() => {
                            // Silently swallow: the service-worker / network already denied it.
                            // onAuthStateChanged will fire naturally once connectivity returns.
                        });
                    }
                    // If it IS a network error, do nothing — wait for the next interval
                }
            } else if (!auth.currentUser && !currentUser) {
                // Both agree there is no user — reset the error-logged flag so we
                // produce fresh logs if the inconsistency appears again later.
                periodicErrorLoggedRef.current = false;
            }
        }, 30000); // Every 30 seconds

        return () => clearInterval(intervalId);
    }, [currentUser]);
}

export default useAuthRecovery;
